import { Badge, Box, Heading, Text } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { Fragment, useMemo } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { HighlightedText } from "../../components/highlighted-text";
import { NoteSnippetsDrawer } from "../../components/note-snippets-drawer";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { createDiagnosticReportFilters } from "../../utils/diagnostic-report-filters";
import { createNoteMatchData } from "../../utils/note-processing";
import { TableRow } from "../../utils/tables-metadata";
import { DiagnosticReportRowData } from "./diagnostic-reports-rows";

export type NotesTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  resourceType: "DiagnosticReport" | "ImagingReports";
  displayName: string;
  onPdfView?: (docId: string) => void;
};

export function NotesTable({ rows, onRowClick, filterQuery, displayName }: NotesTableProps) {
  const ehrContext = useEhrContext();

  const columns = useMemo<ColumnDef<DiagnosticReportRowData>[]>(() => {
    const cols: ColumnDef<DiagnosticReportRowData>[] = [
      { key: "typeOfReport", header: "Note Type" },
      { key: "noteCategory", header: "Category" },
      { key: "specialty", header: "Specialty" },
      { key: "reasonForVisit", header: "Reason For Visit" },
      { key: "location", header: "Location" },
      { key: "date", header: "Date", maxWidth: "150px" },
    ];

    const ehrColumn = createEhrWriteBackColumn<DiagnosticReportRowData>(ehrContext, "note");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);

  const filterOptions = useMemo(() => {
    const diagnosticRows = rows as DiagnosticReportRowData[];
    const filters = createDiagnosticReportFilters(diagnosticRows);
    return filters.map(option => ({
      label: option.label,
      groupLabel: option.groupLabel,
      isChecked: true, // All filters enabled by default
      filterFn: (rowData: DiagnosticReportRowData) => {
        const [type, value] = option.value.split(":");
        switch (type) {
          case "category":
            if (value === "undefined") {
              return !rowData.noteCategory || rowData.noteCategory === "-";
            }
            return rowData.noteCategory === value;
          case "specialty":
            if (value === "undefined") {
              return !rowData.specialty || rowData.specialty === "-";
            }
            return rowData.specialty === value;
          default:
            return true;
        }
      },
    }));
  }, [rows]);

  const { activeFilters, setActiveFilters, filteredRows } = useTableFilters(rows, filterOptions);

  function handleRowClick(row: DiagnosticReportRowData) {
    onRowClick(row);
  }

  function hasNoteMatches(row: TableRow): boolean {
    const noteData = row.noteData as { fullText: string } | undefined;
    if (!noteData?.fullText || !filterQuery.trim()) return false;
    const matchData = createNoteMatchData(noteData.fullText, filterQuery);
    return Boolean(matchData && matchData.count > 0);
  }

  const hasSearchQuery = Boolean(filterQuery.trim());

  if (!hasSearchQuery) {
    return (
      <BaseTableLayout
        title={displayName}
        rows={filteredRows as DiagnosticReportRowData[]}
        columns={columns}
        filterQuery={filterQuery}
        onRowClick={handleRowClick}
        headerActionsSlot={
          filterOptions.length > 0 ? (
            <TableFilterPopover
              filters={filterOptions}
              activeFilters={activeFilters}
              setActiveFilters={newFilters => setActiveFilters(newFilters)}
            />
          ) : undefined
        }
      />
    );
  }

  // Card-style layout with note snippets when searching
  return (
    <Box
      w="100%"
      mb={2}
      borderRadius="lg"
      bg="bg.surface"
      px={6}
      py={6}
      border="1px"
      borderColor="border"
      boxShadow="sm"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md" fontWeight="semibold" color="fg">
          {displayName} ({filteredRows.length})
        </Heading>
        {filterOptions.length > 0 && (
          <TableFilterPopover
            filters={filterOptions}
            activeFilters={activeFilters}
            setActiveFilters={newFilters => setActiveFilters(newFilters)}
          />
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {filteredRows.map(row => {
          const noteRow = row as unknown as DiagnosticReportRowData;
          const hasMatches = hasNoteMatches(row);

          return (
            <Fragment key={row.id}>
              <Box
                borderRadius="md"
                borderWidth="1px"
                borderColor="border"
                boxShadow="sm"
                overflow="hidden"
                bg="bg.canvas"
              >
                {/* Main row - clickable */}
                <Box
                  cursor="pointer"
                  onClick={() => handleRowClick(noteRow)}
                  _hover={{ bg: "interactive.hover" }}
                  p={4}
                >
                  <Box display="grid" gridTemplateColumns="repeat(6, 1fr)" gap={4}>
                    <Box>
                      {hasMatches ? (
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Text fontSize="sm" fontWeight="semibold" color="fg">
                            <HighlightedText
                              text={noteRow.typeOfReport}
                              searchQuery={filterQuery}
                            />
                          </Text>
                          <Badge
                            size="xs"
                            p={0}
                            bg="transparent"
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            w="fit-content"
                            fontSize="2xs"
                            opacity={0.7}
                          >
                            Note excerpts below
                          </Badge>
                        </Box>
                      ) : (
                        <Text fontSize="sm" color="fg">
                          <HighlightedText text={noteRow.typeOfReport} searchQuery={filterQuery} />
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={noteRow.noteCategory} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={noteRow.specialty} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={noteRow.reasonForVisit} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={noteRow.location} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="fg">
                        <HighlightedText text={noteRow.date} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                  </Box>
                </Box>

                {/* Note snippets drawer */}
                {hasMatches && (
                  <NoteSnippetsDrawer
                    noteData={row.noteData as { fullText: string } | undefined}
                    filterQuery={filterQuery}
                    rowId={row.id}
                    onSnippetClick={() => handleRowClick(noteRow)}
                  />
                )}
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
